import {
  PlanTypeCardMismatchKind,
  type PlanTypeCardPartner,
  type PlanTypeCardPartnerMismatch,
  type PlanTypeCardPlanRecord,
} from '@ebalance/proto/bffuser/v1/submission_management_model_pb'

import { formatKomaRange } from '~/models/komaRange'
import {
  type PlanDetailMismatch,
  buildNoSubmissionRecordPlanUrl,
  buildPlanDetailUrl,
} from '~/pages/SubmissionManagement/models/planDetailUrl'

/** 取引相手間ミスマッチ (kind == COUNTERPARTY) の要素。無ければ undefined。 */
export function findCounterpartyMismatch(
  partner: PlanTypeCardPartner,
): PlanTypeCardPartnerMismatch | undefined {
  return partner.mismatches.find(
    (m) => m.kind === PlanTypeCardMismatchKind.COUNTERPARTY,
  )
}

/**
 * 遷移 URL に渡す mismatch (AC-PSM-038-1)。COUNTERPARTY 要素に範囲が 1 つ以上あり、
 * その根拠提出計画が代表提出計画と一致する時だけ、最初の範囲を返す。
 * 照合版の無い不整合通知由来 (from_unmatched_notice、AC-PSM-078) は根拠版が空で
 * 来るため、一致を見ずに範囲を返す (遷移先は呼び出し元が渡す代表の版、代表の版が無い
 * 〈undefined〉時は eBalance に提出記録の無い計画の詳細)。
 * それ以外 (一致・状態なし・代表と別断面・範囲なし) は undefined。
 */
export function deriveDetailMismatch(
  partner: PlanTypeCardPartner,
  representativePlanSubmissionId: string | undefined,
  side: PlanDetailMismatch['side'],
): PlanDetailMismatch | undefined {
  const mismatch = findCounterpartyMismatch(partner)
  const first = mismatch?.ranges[0]
  if (
    mismatch === undefined ||
    first === undefined ||
    (!mismatch.fromUnmatchedNotice &&
      mismatch.planSubmissionId !== representativePlanSubmissionId)
  ) {
    return undefined
  }
  const { from, to } = formatKomaRange(first)
  return { from, to, partnerCode: partner.counterpartyCode, side }
}

/**
 * 計画レコードの取引先カードの遷移先 (AC-PSM-038)。
 *
 * - 代表の版がある: その版の詳細
 * - 代表の版が無く、カードが「eBalance での提出記録なし」(AC-PSM-078、
 *   `submission_summary.no_submission_record`): 業務キー (対象日・代表 BG コード =
 *   計画提出者コード) で開く提出記録の無い計画の詳細 (AC-PPD-028)。不整合の表示の
 *   有無・解消済みかによらずリンクする (解消後も対応メモを追記できる。spec §7)
 * - それ以外 (版も提出記録なしの通知も無い): リンクなし
 */
export type PlanRecordLink =
  | { kind: 'version'; planSubmissionId: string }
  | {
      kind: 'noSubmissionRecord'
      targetDate: Date
      submissionEntityCode: string
    }

export function resolvePlanRecordLink(
  record: Pick<
    PlanTypeCardPlanRecord,
    'representativePlanSubmissionId' | 'representativeBgCode'
  >,
  noSubmissionRecord: boolean,
  targetDate: Date,
): PlanRecordLink | undefined {
  if (record.representativePlanSubmissionId !== undefined) {
    return {
      kind: 'version',
      planSubmissionId: record.representativePlanSubmissionId,
    }
  }
  if (!noSubmissionRecord) return undefined
  return {
    kind: 'noSubmissionRecord',
    targetDate,
    submissionEntityCode: record.representativeBgCode,
  }
}

/**
 * 取引先カードの遷移 URL。ハイライト (AC-PSM-038-1) は版の詳細なら代表の版と同じ断面の
 * 不整合か照合版の無い通知由来の不整合、提出記録の無い計画の詳細なら照合版の無い通知
 * 由来の不整合 (from_unmatched_notice) の時だけ付ける (`deriveDetailMismatch`)。
 */
export function buildPartnerCardUrl(
  link: PlanRecordLink,
  partner: PlanTypeCardPartner,
  side: PlanDetailMismatch['side'],
): string {
  if (link.kind === 'version') {
    return buildPlanDetailUrl(
      link.planSubmissionId,
      deriveDetailMismatch(partner, link.planSubmissionId, side),
    )
  }
  return buildNoSubmissionRecordPlanUrl(
    link.targetDate,
    link.submissionEntityCode,
    deriveDetailMismatch(partner, undefined, side),
  )
}
